import { ElementType } from 'react';
import { css } from '@emotion/react';
import { TemplateType_Slug } from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { TemplatorV2Hooks } from '@frontend/api-templator-v2';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { IconButton, PolymorphicComponentPropWithoutRef, Text, useTooltip } from '@frontend/design-system';

type TemplateListItemProps = {
  groupIds: string[];
  title: string;
  templateString: string;
  templateTypeSlug: TemplateType_Slug;
  onEditButtonClick?: (e: React.MouseEvent) => void;
  hideEditButton?: boolean;
  trackingId?: string;
  showChevron?: boolean;
  isDisabled?: boolean;
  disabledLabel?: string;
};

type PolymorphicProps<C extends ElementType> = PolymorphicComponentPropWithoutRef<C, TemplateListItemProps>;

export const TemplateListItem = <C extends ElementType = 'li'>({
  groupIds,
  as,
  title,
  templateString,
  templateTypeSlug,
  onEditButtonClick,
  hideEditButton,
  trackingId,
  showChevron,
  isDisabled,
  disabledLabel,
  ...rest
}: PolymorphicProps<C>) => {
  const { t } = useTranslation('integrated-messaging');
  const Component = as || 'li';
  const isActionable = !!rest.onClick || !!rest.onClickCapture;
  const transformTemplateString = TemplatorV2Hooks.useTransformStringWithDynamicFieldsToLabels({
    businessGroupIds: groupIds,
    templateTypeSlugs: [templateTypeSlug],
  });
  const transformedTemplateString = transformTemplateString(templateString);
  const { Tooltip, tooltipProps, triggerProps } = useTooltip({
    placement: 'top',
  });

  return (
    <>
      <Component
        css={[
          {
            display: 'flex',
            borderBottom: `1px solid ${theme.colors.neutral10}`,
            width: '100%',
            justifyContent: 'space-between',
            padding: theme.spacing(1.5, 3),
          },
          isActionable &&
            !isDisabled && {
              cursor: 'pointer',
              ':hover': {
                backgroundColor: theme.colors.neutral5,
              },
              ':focus': {
                outline: 'none',
                boxShadow: `2px 0 0 ${theme.colors.primary50} inset`,
                backgroundColor: theme.colors.neutral5,
              },
            },
          isDisabled && {
            opacity: 0.5,
          },
        ]}
        tabIndex={isActionable ? 0 : undefined}
        data-trackingid={trackingId}
        {...rest}
      >
        <div css={{ flex: 1 }} {...triggerProps}>
          <Text weight='bold' size='medium' css={{ marginBottom: theme.spacing(0.5), lineHeight: theme.spacing(2.5) }}>
            {title}
          </Text>
          <Text
            color='light'
            size='small'
            css={css`
              display: -webkit-box;
              -webkit-box-orient: vertical;
              -webkit-line-clamp: 2;
              overflow: hidden;
              line-height: ${theme.spacing(2)};
            `}
          >
            {transformedTemplateString}
          </Text>
        </div>
        <div css={{ display: 'flex', alignItems: 'center' }}>
          {!hideEditButton && onEditButtonClick && (
            <IconButton
              label={t('Edit template {{templateName}}', { templateName: title })}
              onClick={(e) => onEditButtonClick(e)}
              css={{
                ':hover:not(:disabled)': {
                  backgroundColor: theme.colors.white,
                },
              }}
            >
              <Icon name='edit-small' />
            </IconButton>
          )}
          <Icon name='caret-right-small' size={16} css={!showChevron && { visibility: 'hidden' }} />
        </div>
      </Component>
      {isDisabled && !!disabledLabel && <Tooltip {...tooltipProps}>{disabledLabel}</Tooltip>}
    </>
  );
};
